import React, { useState, useContext, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../App';
import axios from 'axios';
import { 
  ArrowLeft, 
  Clock, 
  Target, 
  CheckCircle, 
  XCircle, 
  Trophy, 
  RefreshCw,
  Star,
  BookOpen,
  TrendingUp,
  Eye,
  EyeOff,
  Image,
  Lightbulb,
  Brain,
  PenTool
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SureyeGame = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [gameState, setGameState] = useState('menu'); // menu, learning, storytelling, testing, completed
  const [sessionId, setSessionId] = useState(null);
  const [currentStoryIndex, setCurrentStoryIndex] = useState(0);
  const [stories, setStories] = useState([]);
  const [learnedStories, setLearnedStories] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [score, setScore] = useState({ correct: 0, total: 0 });
  const [timeLeft, setTimeLeft] = useState(45);
  const [gameResults, setGameResults] = useState(null);
  const [difficulty, setDifficulty] = useState('beginner');
  const [loading, setLoading] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [lastAnswerCorrect, setLastAnswerCorrect] = useState(null);
  const [showMeaning, setShowMeaning] = useState(false);
  const [showFullStory, setShowFullStory] = useState(false);
  const [userRetelling, setUserRetelling] = useState('');
  const [storytellingMode, setStorytellingMode] = useState(false);
  const [contentType, setContentType] = useState('patakines'); // 'patakines' or 'sureyes'

  const difficulties = [
    {
      value: 'beginner',
      label: 'Principiante',
      description: 'Sureyes básicos y patakíes fundamentales',
      icon: '📖',
      color: 'from-blue-500 to-indigo-600',
      storyCount: 16
    },
    {
      value: 'intermediate', 
      label: 'Intermedio',
      description: 'Historias complejas y enseñanzas profundas',
      icon: '📚',
      color: 'from-purple-500 to-violet-600',
      storyCount: 32
    },
    {
      value: 'advanced',
      label: 'Avanzado',
      description: 'Patakíes completos y múltiples versiones',
      icon: '📜',
      color: 'from-amber-500 to-orange-600',
      storyCount: 64
    },
    {
      value: 'expert',
      label: 'Experto',
      description: 'Dominio completo de tradiciones orales',
      icon: '👑',
      color: 'from-red-500 to-pink-600',
      storyCount: 100
    }
  ];

  // Sample Sureyes (Songs) - Sacred chants and songs
  const sampleSureyes = [
    {
      id: 1,
      title: "Sureye de Eji Ogbe",
      category: "Sureye Sagrado",
      difficulty: "beginner",
      ifa_sign: "Eji Ogbe",
      oddun_symbol: "I I I I\nI I I I",
      song_yoruba: "Eji Ogbe ni wa o\nOba ati Olugbon\nAiye mi dun o\nEji Ogbe ni wa o",
      song_spanish: "Eji Ogbe está aquí\nRey y Señor noble\nMi vida es dulce\nEji Ogbe está aquí",
      rhythm: "Moderado, ceremonial",
      instruments: ["Tambor batá", "Chekeré", "Claves"],
      purpose: "Invocar prosperidad y liderazgo",
      when_to_sing: "Al amanecer, ceremonias de iniciación",
      meaning: "Canto para llamar la luz y la prosperidad a nuestras vidas"
    },
    {
      id: 2,
      title: "Sureye de Yemaya",
      category: "Sureye de Orisha",
      difficulty: "beginner", 
      ifa_sign: "Okana Meji",
      oddun_symbol: "I II II II\nI II II II",
      song_yoruba: "Yemaya asesu\nOmo eja tutu\nMama Yemaya\nOmo re wa si le",
      song_spanish: "Yemaya de las aguas cristalinas\nMadre de peces frescos\nMamá Yemaya\nTus hijos vienen a casa",
      rhythm: "Fluido como las olas",
      instruments: ["Tambores", "Maracas", "Caracola"],
      purpose: "Honrar a la madre de las aguas",
      when_to_sing: "Cerca del mar, rituales de purificación",
      meaning: "Canto de conexión con la energía maternal del océano"
    }
  ];

  // Sample Patakines (Stories) - Sacred narratives
  const samplePatakines = [
    {
      id: 1,
      title: "El Caracol de Yemaya",
      category: "Patakí de Yemaya",
      difficulty: "beginner",
      ifa_sign: "Okana Meji",
      oddun_symbol: "I II II II\nI II II II",
      sureye: "Okana dice que las aguas son la fuente de la vida",
      visual_elements: {
        main_image: "🌊🐚",
        characters: [
          { name: "Yemaya", symbol: "👩🏾‍💼", description: "Madre de las aguas" },
          { name: "Pescador", symbol: "🧙🏾‍♂️", description: "Hombre humilde" },
          { name: "Caracol Sagrado", symbol: "🐚", description: "Mensajero divino" }
        ],
        setting: { name: "Orilla del océano", symbol: "🏖️" },
        symbols: [
          { name: "Agua", symbol: "💧" },
          { name: "Luna", symbol: "🌙" }, 
          { name: "Pureza", symbol: "⚪" }
        ]
      },
      story_short: "Un pescador pobre encuentra un caracol que le habla con la voz de Yemaya, quien le enseña los secretos de la pesca y la abundancia.",
      story_full: "Había una vez un pescador muy pobre que cada día salía al mar sin pescar nada. Un día encontró un hermoso caracol en la orilla. Al tomarlo, escuchó una voz melodiosa que decía: 'Soy Yemaya, madre de las aguas. Si me respetas y cuidas el océano, te daré abundancia.' El pescador prometió cuidar las aguas y desde ese día, siempre tuvo pesca abundante, pero nunca pescó más de lo necesario.",
      moral: "La generosidad de la naturaleza se obtiene con respeto y moderación",
      teaching: "Yemaya protege a quienes respetan las aguas y la vida marina",
      life_application: "Debemos cuidar los recursos naturales y no ser codiciosos",
      keywords: ["abundancia", "respeto", "naturaleza", "Yemaya", "océano"]
    }
  ];
  const sampleStories = [
    {
      id: 1,
      title: "El Caracol de Yemaya",
      category: "Patakí de Yemaya",
      difficulty: "beginner",
      ifa_sign: "Okana Meji",
      oddun_symbol: "||||  ||||",
      sureye: "Okana dice que las aguas son la fuente de la vida",
      visual_elements: {
        main_image: "🌊🐚",
        characters: [
          { name: "Yemaya", symbol: "👩🏾‍💼", description: "Madre de las aguas" },
          { name: "Pescador", symbol: "🧙🏾‍♂️", description: "Hombre humilde" },
          { name: "Caracol Sagrado", symbol: "🐚", description: "Mensajero divino" }
        ],
        setting: { name: "Orilla del océano", symbol: "🏖️" },
        symbols: [
          { name: "Agua", symbol: "💧" },
          { name: "Luna", symbol: "🌙" }, 
          { name: "Pureza", symbol: "⚪" }
        ]
      },
      story_short: "Un pescador pobre encuentra un caracol que le habla con la voz de Yemaya, quien le enseña los secretos de la pesca y la abundancia.",
      story_full: "Había una vez un pescador muy pobre que cada día salía al mar sin pescar nada. Un día encontró un hermoso caracol en la orilla. Al tomarlo, escuchó una voz melodiosa que decía: 'Soy Yemaya, madre de las aguas. Si me respetas y cuidas el océano, te daré abundancia.' El pescador prometió cuidar las aguas y desde ese día, siempre tuvo pesca abundante, pero nunca pescó más de lo necesario.",
      moral: "La generosidad de la naturaleza se obtiene con respeto y moderación",
      teaching: "Yemaya protege a quienes respetan las aguas y la vida marina",
      life_application: "Debemos cuidar los recursos naturales y no ser codiciosos",
      keywords: ["abundancia", "respeto", "naturaleza", "Yemaya", "océano"],
      questions: [
        {
          type: "comprehension",
          question: "¿Qué encontró el pescador en la orilla?",
          options: ["Un pez dorado", "Un caracol que hablaba", "Una perla", "Una red mágica"],
          correct: "Un caracol que hablaba"
        },
        {
          type: "moral",
          question: "¿Cuál es la enseñanza principal del patakí?",
          options: ["Pescar mucho", "Respetar la naturaleza", "Ser rico", "Vivir cerca del mar"],
          correct: "Respetar la naturaleza"
        },
        {
          type: "application",
          question: "¿Cómo aplicarías esta enseñanza hoy?",
          options: ["Pescando más", "Cuidando el medio ambiente", "Comprando caracolas", "Nadando mucho"],
          correct: "Cuidando el medio ambiente"
        }
      ]
    },
    {
      id: 2,
      title: "Las Llaves de Eleggua",
      category: "Patakí de Eleggua",
      difficulty: "beginner",
      ifa_sign: "Eji Ogbe",
      oddun_symbol: "||||  ||||",
      sureye: "Eji Ogbe dice que Eleggua abre todos los caminos",
      visual_elements: {
        main_image: "🗝️⚡",
        characters: [
          { name: "Eleggua", symbol: "👨🏾‍🦳", description: "Guardián de caminos" },
          { name: "Comerciante", symbol: "👨🏾‍💼", description: "Viajero perdido" },
          { name: "Puertas", symbol: "🚪", description: "Oportunidades" }
        ],
        setting: { name: "Encrucijada al amanecer", symbol: "🌅" },
        symbols: [
          { name: "Llaves", symbol: "🔑" },
          { name: "Estrella", symbol: "⭐" },
          { name: "Camino", symbol: "🛤️" }
        ]
      },
      story_short: "Un comerciante perdido en una encrucijada recibe de Eleggua las llaves que abren todos los caminos de la prosperidad.",
      story_full: "Un comerciante viajaba por caminos desconocidos cuando llegó a una encrucijada donde no sabía qué dirección tomar. Apareció un niño que le dijo: 'Soy Eleggua, guardián de los caminos. Te daré estas llaves mágicas, pero debes usarlas para ayudar a otros, no solo para ti.' El comerciante aceptó y descubrió que las llaves abrían puertas de oportunidades, pero solo cuando las usaba para ayudar a su comunidad.",
      moral: "El verdadero éxito viene cuando ayudamos a otros en su camino",
      teaching: "Eleggua abre caminos a quienes tienen buen corazón",
      life_application: "Las oportunidades se multiplican cuando las compartimos",
      keywords: ["caminos", "llaves", "Eleggua", "oportunidades", "comunidad"],
      questions: [
        {
          type: "comprehension", 
          question: "¿Quién apareció en la encrucijada?",
          options: ["Un anciano sabio", "Eleggua como un niño", "Un guía turístico", "Un policía"],
          correct: "Eleggua como un niño"
        },
        {
          type: "moral",
          question: "¿Cuándo funcionaban las llaves mágicas?",
          options: ["Siempre", "Solo de noche", "Cuando ayudaba a otros", "En domingo"],
          correct: "Cuando ayudaba a otros"
        },
        {
          type: "symbolic",
          question: "¿Qué simbolizan las llaves en este patakí?",
          options: ["Dinero", "Oportunidades", "Problemas", "Secretos"],
          correct: "Oportunidades"
        }
      ]
    },
    {
      id: 3,
      title: "La Corona de Shango",
      category: "Patakí de Shango",
      difficulty: "intermediate",
      ifa_sign: "Shango Meji",
      oddun_symbol: "||||  ||||",
      sureye: "Shango dice que la justicia debe reinar con sabiduría",
      visual_elements: {
        main_image: "👑⚡",
        characters: [
          { name: "Shango", symbol: "🤴🏾", description: "Rey de la justicia" },
          { name: "Juez injusto", symbol: "⚖️", description: "Autoridad corrupta" },
          { name: "Pueblo", symbol: "👥", description: "Ciudadanos oprimidos" }
        ],
        setting: { name: "Palacio real", symbol: "🏛️" },
        symbols: [
          { name: "Rayo", symbol: "⚡" },
          { name: "Corona", symbol: "👑" },
          { name: "Balanza", symbol: "⚖️" }
        ]
      },
      story_short: "Shango enseña a un juez corrupto que el verdadero poder viene de la justicia, no de la posición.",
      story_full: "En un reino había un juez que favorecía a los ricos y castigaba injustamente a los pobres. Shango, rey de la justicia, bajó del cielo disfrazado de campesino pobre. Cuando el juez lo trató mal, Shango reveló su identidad con truenos y rayos. Le mostró que su corona no tenía poder sin justicia. El juez aprendió que el verdadero poder viene de servir con equidad a todos.",
      moral: "La autoridad sin justicia es tiranía",
      teaching: "Shango representa la justicia divina que protege a los desamparados",
      life_application: "Usar cualquier posición de poder para ayudar, no para oprimir",
      keywords: ["justicia", "poder", "Shango", "equidad", "responsabilidad"],
      questions: [
        {
          type: "comprehension",
          question: "¿Cómo se disfrazó Shango?",
          options: ["De rico comerciante", "De campesino pobre", "De otro juez", "De soldado"],
          correct: "De campesino pobre"
        },
        {
          type: "moral",
          question: "¿Qué aprendió el juez?",
          options: ["A ser más rico", "Que el poder requiere justicia", "A temer los rayos", "A juzgar rápido"],
          correct: "Que el poder requiere justicia"
        },
        {
          type: "application",
          question: "Si tuvieras autoridad, ¿cómo aplicarías esta enseñanza?",
          options: ["Favoreciendo amigos", "Siendo justo con todos", "Cobrando más", "Ignorando problemas"],
          correct: "Siendo justo con todos"
        }
      ]
    }
  ];

  // Timer effect
  useEffect(() => {
    if (gameState === 'testing' && timeLeft > 0 && !selectedAnswer) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && !selectedAnswer) {
      handleAnswerSubmit(''); 
    }
  }, [gameState, timeLeft, selectedAnswer]);

  const startLearning = async (selectedDifficulty) => {
    setLoading(true);
    try {
      // Filter stories by difficulty
      const filteredStories = sampleStories.filter(story => 
        story.difficulty === selectedDifficulty || selectedDifficulty === 'expert'
      );
      
      setStories(selectedDifficulty === 'expert' ? sampleStories : filteredStories);
      setDifficulty(selectedDifficulty);
      setGameState('learning');
      setCurrentStoryIndex(0);
      setLearnedStories([]);
      
      toast.success('¡Comienza tu aprendizaje de patakíes!');
    } catch (error) {
      console.error('Error starting game:', error);
      toast.error('Error al iniciar el aprendizaje');
    } finally {
      setLoading(false);
    }
  };

  const markStoryAsLearned = (story) => {
    if (!learnedStories.find(s => s.id === story.id)) {
      setLearnedStories(prev => [...prev, story]);
      toast.success(`${story.title} agregado a historias aprendidas`);
    }
  };

  const startStorytelling = (story) => {
    setStorytellingMode(true);
    setUserRetelling('');
    setShowFullStory(false);
    toast.success('Usa la técnica de Feynman: explica la historia con tus propias palabras');
  };

  const submitRetelling = () => {
    if (userRetelling.length < 50) {
      toast.error('Tu narración debe ser más detallada (mínimo 50 caracteres)');
      return;
    }
    
    const currentStory = stories[currentStoryIndex];
    markStoryAsLearned(currentStory);
    
    // Simple keyword analysis for feedback
    const keywords = currentStory.keywords;
    const mentionedKeywords = keywords.filter(keyword => 
      userRetelling.toLowerCase().includes(keyword.toLowerCase())
    );
    
    const percentage = (mentionedKeywords.length / keywords.length) * 100;
    
    if (percentage >= 80) {
      toast.success('¡Excelente narración! Captaste todos los elementos clave');
    } else if (percentage >= 60) {
      toast.success('¡Buena narración! Incluiste la mayoría de elementos importantes');
    } else {
      toast.info('Buena historia, pero podrías mencionar más elementos clave');
    }
    
    setStorytellingMode(false);
    setUserRetelling('');
  };

  const generateTest = () => {
    if (learnedStories.length < 2) {
      toast.error('Aprende al menos 2 historias antes de la prueba');
      return;
    }
    
    const questions = generateStoryQuestions(learnedStories);
    setStories(questions);
    setGameState('testing');
    setScore({ correct: 0, total: 0 });
    setCurrentStoryIndex(0);
    setTimeLeft(45);
  };

  const generateStoryQuestions = (learnedStories) => {
    const questions = [];
    
    learnedStories.forEach((story, index) => {
      // Add multiple questions per story
      story.questions.forEach((q, qIndex) => {
        questions.push({
          id: `${story.id}_${qIndex}`,
          story_title: story.title,
          story_visual: story.visual_elements.main_image,
          question: q.question,
          type: q.type,
          options: q.options,
          correct_answer: q.correct,
          story_context: story.story_short,
          moral: story.moral
        });
      });
    });
    
    return questions.sort(() => Math.random() - 0.5).slice(0, 15); // Max 15 questions
  };

  const nextStory = () => {
    if (currentStoryIndex < stories.length - 1) {
      setCurrentStoryIndex(prev => prev + 1);
      setShowMeaning(false);
      setShowFullStory(false);
    } else {
      toast.info('Has completado todas las historias disponibles');
    }
  };

  const previousStory = () => {
    if (currentStoryIndex > 0) {
      setCurrentStoryIndex(prev => prev - 1);
      setShowMeaning(false);
      setShowFullStory(false);
    }
  };

  const handleAnswerSelect = (answer) => {
    if (!selectedAnswer && timeLeft > 0) {
      setSelectedAnswer(answer);
      setTimeout(() => handleAnswerSubmit(answer), 500);
    }
  };

  const handleAnswerSubmit = async (answer) => {
    const question = stories[currentStoryIndex];
    if (!question) return;
    
    const isCorrect = answer === question.correct_answer;
    setLastAnswerCorrect(isCorrect);
    setShowResult(true);
    
    if (isCorrect) {
      setScore(prev => ({ ...prev, correct: prev.correct + 1 }));
    }
    setScore(prev => ({ ...prev, total: prev.total + 1 }));
    
    // Show result for 3 seconds (longer for story context)
    setTimeout(() => {
      if (score.total + 1 < stories.length) {
        setCurrentStoryIndex(prev => prev + 1);
        setSelectedAnswer('');
        setTimeLeft(45);
        setShowResult(false);
        setLastAnswerCorrect(null);
      } else {
        completeGame();
      }
    }, 3000);
  };

  const completeGame = async () => {
    const percentage = Math.round((score.correct / stories.length) * 100);
    
    setGameResults({
      score: percentage,
      correct_answers: score.correct,
      total_questions: stories.length,
      percentage: percentage,
      stories_learned: learnedStories.length
    });
    setGameState('completed');
    
    if (percentage >= 80) {
      toast.success('¡Excelente comprensión de las enseñanzas!');
    } else if (percentage >= 60) {
      toast.success('¡Buen entendimiento de los patakíes!');
    } else {
      toast.success('¡Sigue estudiando las historias sagradas!');
    }
  };

  const resetGame = () => {
    setGameState('menu');
    setSessionId(null);
    setStories([]);
    setLearnedStories([]);
    setCurrentStoryIndex(0);
    setScore({ correct: 0, total: 0 });
    setTimeLeft(45);
    setSelectedAnswer('');
    setGameResults(null);
    setShowResult(false);
    setLastAnswerCorrect(null);
    setShowMeaning(false);
    setShowFullStory(false);
    setUserRetelling('');
    setStorytellingMode(false);
  };

  const currentStory = stories[currentStoryIndex];

  // Menu Screen
  if (gameState === 'menu') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50">
        <div className="container mx-auto px-6 py-8">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <Link 
              to="/dashboard"
              className="p-2 bg-white rounded-xl shadow-lg text-purple-600 hover:text-purple-800 transition-colors"
            >
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <div>
              <h1 className="font-cinzel text-3xl font-bold text-purple-800">Sureye y Patakíes</h1>
              <p className="text-purple-600">Aprende las historias sagradas con técnicas de narrativa y dual coding</p>
            </div>
          </div>

          {/* Difficulty Selection */}
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="font-cinzel text-2xl font-bold text-purple-800 mb-4">Selecciona tu Nivel de Estudio</h2>
              <p className="text-purple-700 max-w-2xl mx-auto">
                Aprende las historias sagradas y sus enseñanzas profundas 
                para comprender la sabiduría ancestral de Ifá.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              {difficulties.map((diff, index) => (
                <div 
                  key={index} 
                  className={`game-card bg-gradient-to-br ${diff.color} p-8 rounded-2xl text-white cursor-pointer relative overflow-hidden`}
                  onClick={() => startLearning(diff.value)}
                >
                  <div className="text-6xl mb-4 text-center">{diff.icon}</div>
                  <h3 className="font-cinzel text-2xl font-bold mb-3 text-center">{diff.label}</h3>
                  <p className="text-white text-opacity-90 text-center leading-relaxed mb-4">
                    {diff.description}
                  </p>
                  <div className="text-center text-white text-opacity-80 text-sm mb-6">
                    ~{diff.storyCount} historias • Aprendizaje visual
                  </div>
                  
                  <div className="text-center">
                    <button 
                      className="bg-white text-purple-800 px-6 py-3 rounded-xl font-semibold hover:scale-105 transition-all duration-300 flex items-center gap-2 mx-auto"
                      disabled={loading}
                    >
                      {loading ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-800"></div>
                      ) : (
                        <>
                          <BookOpen className="w-5 h-5" />
                          Comenzar Historias
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Learning Screen - Dual Coding with Visual + Narrative
  if (gameState === 'learning') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-800 to-blue-700">
        <div className="container mx-auto px-6 py-8">
          {/* Learning Header */}
          <div className="flex items-center justify-between mb-8 text-white">
            <div className="flex items-center gap-4">
              <button 
                onClick={resetGame}
                className="p-2 bg-white bg-opacity-20 rounded-xl hover:bg-opacity-30 transition-all"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div>
                <h1 className="font-cinzel text-2xl font-bold">Patakíes y Sureyes</h1>
                <p className="text-purple-200 capitalize">{difficulty} • Codificación Dual</p>
              </div>
            </div>
            
            <div className="flex items-center gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold">{learnedStories.length}</div>
                <div className="text-purple-200 text-sm">Aprendidas</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">{currentStoryIndex + 1}/{stories.length}</div>
                <div className="text-purple-200 text-sm">Progreso</div>
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex justify-between text-purple-200 text-sm mb-2">
              <span>Historia {currentStoryIndex + 1} de {stories.length}</span>
              <span>{Math.round(((currentStoryIndex + 1) / stories.length) * 100)}% Completado</span>
            </div>
            <div className="progress-bar bg-purple-800">
              <div 
                className="progress-fill bg-gradient-to-r from-pink-400 to-purple-400" 
                style={{ width: `${((currentStoryIndex + 1) / stories.length) * 100}%` }}
              ></div>
            </div>
          </div>

          {/* Quick Actions */}
          {learnedStories.length >= 2 && (
            <div className="flex justify-center mb-8">
              <button
                onClick={generateTest}
                className="bg-pink-500 text-white px-6 py-3 rounded-xl font-semibold hover:bg-pink-600 transition-colors flex items-center gap-2"
              >
                <Brain className="w-5 h-5" />
                Prueba de Comprensión ({learnedStories.length} historias)
              </button>
            </div>
          )}

          {/* Story Card with Dual Coding */}
          {currentStory && (
            <div className="max-w-4xl mx-auto">
              <div className="bg-white rounded-2xl p-8 shadow-2xl mb-6">
                {/* Visual Header */}
                <div className="text-center mb-8">
                  <div className="text-8xl mb-4">{currentStory.visual_elements.main_image}</div>
                  <h2 className="font-cinzel text-3xl font-bold text-purple-800 mb-2">
                    {currentStory.title}
                  </h2>
                  <div className="flex justify-center gap-3 mb-4">
                    <span className="bg-purple-100 text-purple-800 px-4 py-2 rounded-full text-sm font-semibold">
                      {currentStory.category}
                    </span>
                    <span className="bg-indigo-100 text-indigo-800 px-4 py-2 rounded-full text-sm font-semibold">
                      {currentStory.ifa_sign}
                    </span>
                  </div>
                  
                  {/* Oddun Symbol Display */}
                  <div className="mb-6 text-center">
                    <div className="bg-gray-100 p-4 rounded-xl inline-block">
                      <div className="text-2xl font-mono font-bold text-gray-800 mb-2">
                        {currentStory.oddun_symbol}
                      </div>
                      <div className="text-sm text-gray-600">Signo del Oddun</div>
                    </div>
                  </div>
                </div>

                {/* Visual Elements - Dual Coding Technique */}
                <div className="grid md:grid-cols-3 gap-6 mb-8">
                  <div className="text-center p-4 bg-purple-50 rounded-xl">
                    <h4 className="font-bold text-purple-800 mb-3 flex items-center justify-center gap-2">
                      <Image className="w-5 h-5" />
                      Personajes
                    </h4>
                    <div className="space-y-2">
                      {currentStory.visual_elements.characters.map((char, index) => (
                        <div key={index} className="text-center">
                          <div className="text-2xl mb-1">{char.symbol}</div>
                          <div className="text-xs font-semibold text-purple-700">{char.name}</div>
                          <div className="text-xs text-purple-600">{char.description}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="text-center p-4 bg-indigo-50 rounded-xl">
                    <h4 className="font-bold text-indigo-800 mb-3">Escenario</h4>
                    <div className="text-4xl mb-2">{currentStory.visual_elements.setting.symbol}</div>
                    <div className="text-sm font-semibold text-indigo-700">{currentStory.visual_elements.setting.name}</div>
                  </div>
                  
                  <div className="text-center p-4 bg-pink-50 rounded-xl">
                    <h4 className="font-bold text-pink-800 mb-3">Símbolos Sagrados</h4>
                    <div className="space-y-2">
                      {currentStory.visual_elements.symbols.map((symbol, index) => (
                        <div key={index} className="text-center">
                          <div className="text-2xl mb-1">{symbol.symbol}</div>
                          <div className="text-xs font-semibold text-pink-700">{symbol.name}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Sureye */}
                <div className="mb-6 p-4 bg-yellow-50 rounded-xl border-l-4 border-yellow-400">
                  <h4 className="font-bold text-yellow-800 mb-2 flex items-center gap-2">
                    <Star className="w-5 h-5" />
                    Sureye (Enseñanza del Signo)
                  </h4>
                  <p className="text-yellow-700 italic">"{currentStory.sureye}"</p>
                </div>

                {/* Story Content */}
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-lg text-purple-800 flex items-center gap-2">
                      <BookOpen className="w-5 h-5" />
                      Historia
                    </h3>
                    <button
                      onClick={() => setShowFullStory(!showFullStory)}
                      className="flex items-center gap-2 text-purple-600 hover:text-purple-800 transition-colors"
                    >
                      {showFullStory ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      {showFullStory ? 'Ocultar historia completa' : 'Ver historia completa'}
                    </button>
                  </div>
                  
                  <div className="p-6 bg-gray-50 rounded-xl">
                    <p className="text-gray-800 leading-relaxed mb-4">
                      <strong>Resumen:</strong> {currentStory.story_short}
                    </p>
                    
                    {showFullStory && (
                      <div className="border-t pt-4">
                        <p className="text-gray-700 leading-relaxed">
                          {currentStory.story_full}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Storytelling Mode - Feynman Technique */}
                {storytellingMode ? (
                  <div className="mb-8 p-6 bg-blue-50 rounded-xl">
                    <h4 className="font-bold text-blue-800 mb-4 flex items-center gap-2">
                      <PenTool className="w-5 h-5" />
                      Técnica de Feynman: Cuenta la historia con tus palabras
                    </h4>
                    <textarea
                      value={userRetelling}
                      onChange={(e) => setUserRetelling(e.target.value)}
                      className="w-full h-32 p-4 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-400"
                      placeholder="Cuenta esta historia como si se la explicaras a un niño de 10 años. Incluye los personajes principales, qué pasó, y la enseñanza..."
                    />
                    <div className="flex justify-between items-center mt-4">
                      <span className="text-sm text-blue-600">
                        {userRetelling.length} caracteres (mínimo 50)
                      </span>
                      <div className="flex gap-3">
                        <button
                          onClick={() => setStorytellingMode(false)}
                          className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                        >
                          Cancelar
                        </button>
                        <button
                          onClick={submitRetelling}
                          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                        >
                          Enviar Narración
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="mb-8">
                    <button
                      onClick={() => startStorytelling(currentStory)}
                      className="w-full p-4 bg-blue-100 hover:bg-blue-200 rounded-xl border-2 border-blue-300 transition-colors"
                    >
                      <div className="flex items-center justify-center gap-3 text-blue-800">
                        <PenTool className="w-6 h-6" />
                        <div className="text-left">
                          <div className="font-bold">Practica con Técnica de Feynman</div>
                          <div className="text-sm">Explica esta historia con tus propias palabras</div>
                        </div>
                      </div>
                    </button>
                  </div>
                )}

                {/* Teachings and Applications */}
                <div className="grid md:grid-cols-2 gap-6 mb-8">
                  <div className="p-4 bg-green-50 rounded-xl">
                    <h4 className="font-bold text-green-800 mb-2 flex items-center gap-2">
                      <Lightbulb className="w-5 h-5" />
                      Enseñanza Moral
                    </h4>
                    <p className="text-green-700">{currentStory.moral}</p>
                  </div>
                  
                  <div className="p-4 bg-orange-50 rounded-xl">
                    <h4 className="font-bold text-orange-800 mb-2">Aplicación en la Vida</h4>
                    <p className="text-orange-700">{currentStory.life_application}</p>
                  </div>
                </div>

                {/* Keywords for Memory */}
                <div className="mb-8 p-4 bg-gray-100 rounded-xl">
                  <h4 className="font-bold text-gray-800 mb-3">Palabras Clave para Recordar</h4>
                  <div className="flex flex-wrap gap-2">
                    {currentStory.keywords.map((keyword, index) => (
                      <span key={index} className="bg-purple-500 text-white px-3 py-1 rounded-full text-sm">
                        {keyword}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap justify-center gap-4">
                  <button
                    onClick={previousStory}
                    disabled={currentStoryIndex === 0}
                    className="px-6 py-3 bg-gray-500 text-white rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600 transition-colors"
                  >
                    Historia Anterior
                  </button>
                  
                  <button
                    onClick={() => markStoryAsLearned(currentStory)}
                    className="px-6 py-3 bg-green-500 text-white rounded-xl font-semibold hover:bg-green-600 transition-colors flex items-center gap-2"
                  >
                    <CheckCircle className="w-5 h-5" />
                    Historia Aprendida
                  </button>
                  
                  <button
                    onClick={nextStory}
                    disabled={currentStoryIndex === stories.length - 1}
                    className="px-6 py-3 bg-purple-500 text-white rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-purple-600 transition-colors"
                  >
                    Siguiente Historia
                  </button>
                </div>
              </div>

              {/* Learned Stories Overview */}
              {learnedStories.length > 0 && (
                <div className="bg-white bg-opacity-10 rounded-2xl p-6">
                  <h3 className="text-white font-bold text-lg mb-4 flex items-center gap-2">
                    <Trophy className="w-5 h-5" />
                    Historias Dominadas ({learnedStories.length})
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {learnedStories.map((story, index) => (
                      <div key={index} className="flex items-center gap-3 p-3 bg-white bg-opacity-20 rounded-xl">
                        <div className="text-2xl">{story.visual_elements.main_image}</div>
                        <div>
                          <div className="text-white font-semibold">{story.title}</div>
                          <div className="text-purple-200 text-sm">{story.category}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Testing Screen - Similar structure to other games
  if (gameState === 'testing') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-800 to-pink-700">
        <div className="container mx-auto px-6 py-8">
          {/* Test Header */}
          <div className="flex items-center justify-between mb-8 text-white">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => setGameState('learning')}
                className="p-2 bg-white bg-opacity-20 rounded-xl hover:bg-opacity-30 transition-all"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div>
                <h1 className="font-cinzel text-2xl font-bold">Evaluación de Comprensión</h1>
                <p className="text-purple-200">Demuestra tu entendimiento de las enseñanzas</p>
              </div>
            </div>
            
            <div className="flex items-center gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold">{score.correct}/{score.total}</div>
                <div className="text-purple-200 text-sm">Correctas</div>
              </div>
              <div className="text-center">
                <div className={`text-3xl font-bold ${timeLeft <= 15 ? 'text-red-400 animate-pulse' : ''}`}>
                  {timeLeft}
                </div>
                <div className="text-purple-200 text-sm">Segundos</div>
              </div>
            </div>
          </div>

          {/* Test Question with Visual Context */}
          {currentStory && (
            <div className="max-w-4xl mx-auto">
              <div className="bg-white rounded-2xl p-8 shadow-2xl mb-6">
                {/* Visual Context */}
                <div className="text-center mb-6">
                  <div className="text-6xl mb-2">{currentStory.story_visual}</div>
                  <h3 className="text-purple-600 font-semibold">{currentStory.story_title}</h3>
                  <p className="text-gray-600 text-sm italic mt-2">"{currentStory.story_context}"</p>
                </div>
                
                <h2 className="font-cinzel text-2xl font-bold text-purple-800 text-center mb-8">
                  {currentStory.question}
                </h2>
                
                <div className="grid md:grid-cols-2 gap-4">
                  {currentStory.options.map((option, index) => (
                    <button
                      key={index}
                      onClick={() => handleAnswerSelect(option)}
                      disabled={selectedAnswer || timeLeft === 0}
                      className={`p-6 rounded-xl text-left font-semibold transition-all duration-300 border-2 ${
                        selectedAnswer === option
                          ? showResult
                            ? lastAnswerCorrect && selectedAnswer === currentStory.correct_answer
                              ? 'border-green-500 bg-green-50 text-green-800'
                              : 'border-red-500 bg-red-50 text-red-800'
                            : 'border-purple-500 bg-purple-50 text-purple-800'
                          : showResult && option === currentStory.correct_answer
                          ? 'border-green-500 bg-green-50 text-green-800'
                          : 'border-gray-200 bg-gray-50 text-gray-700 hover:border-purple-300 hover:bg-purple-50 hover:text-purple-700'
                      } ${selectedAnswer || timeLeft === 0 ? 'cursor-not-allowed' : 'cursor-pointer hover:scale-[1.02]'}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center text-sm font-bold ${
                          selectedAnswer === option
                            ? showResult
                              ? lastAnswerCorrect && selectedAnswer === currentStory.correct_answer
                                ? 'border-green-500 bg-green-500 text-white'
                                : 'border-red-500 bg-red-500 text-white'
                              : 'border-purple-500 bg-purple-500 text-white'
                            : showResult && option === currentStory.correct_answer
                            ? 'border-green-500 bg-green-500 text-white'
                            : 'border-gray-400 text-gray-600'
                        }`}>
                          {String.fromCharCode(65 + index)}
                        </div>
                        <span>{option}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Result Feedback with Story Context */}
              {showResult && (
                <div className={`text-center p-6 rounded-2xl ${
                  lastAnswerCorrect ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  <div className="flex items-center justify-center gap-3 mb-2">
                    {lastAnswerCorrect ? (
                      <CheckCircle className="w-8 h-8 text-green-600" />
                    ) : (
                      <XCircle className="w-8 h-8 text-red-600" />
                    )}
                    <span className="text-xl font-bold">
                      {lastAnswerCorrect ? '¡Correcto! Entiendes la enseñanza' : 'Incorrecto'}
                    </span>
                  </div>
                  {!lastAnswerCorrect && (
                    <div>
                      <p>La respuesta correcta era: <strong>{currentStory.correct_answer}</strong></p>
                      <p className="text-sm mt-2 italic">Recuerda la enseñanza: {currentStory.moral}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Completed Screen
  if (gameState === 'completed' && gameResults) {
    const getScoreMessage = (percentage) => {
      if (percentage >= 90) return { text: '¡Maestro Narrador! Dominio excepcional de las enseñanzas', icon: '🏆', color: 'text-yellow-600' };
      if (percentage >= 80) return { text: '¡Excelente! Gran comprensión de los patakíes', icon: '⭐', color: 'text-purple-600' };
      if (percentage >= 60) return { text: '¡Buen progreso! Continúa estudiando las historias', icon: '👍', color: 'text-blue-600' };
      return { text: 'Sigue practicando con codificación dual y narrativa', icon: '💪', color: 'text-emerald-600' };
    };

    const scoreMessage = getScoreMessage(gameResults.percentage);

    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center p-6">
        <div className="max-w-2xl w-full">
          <div className="bg-white rounded-2xl p-8 shadow-2xl text-center">
            <div className="mb-8">
              <div className="text-6xl mb-4">{scoreMessage.icon}</div>
              <h1 className="font-cinzel text-3xl font-bold text-purple-800 mb-4">¡Evaluación Completada!</h1>
              <p className={`text-lg font-semibold ${scoreMessage.color}`}>{scoreMessage.text}</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
              <div className="text-center">
                <div className="bg-purple-100 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <Trophy className="w-8 h-8 text-purple-600" />
                </div>
                <div className="text-2xl font-bold text-purple-800">{gameResults.percentage}%</div>
                <div className="text-sm text-gray-600">Comprensión</div>
              </div>
              
              <div className="text-center">
                <div className="bg-green-100 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <div className="text-2xl font-bold text-purple-800">{gameResults.correct_answers}</div>
                <div className="text-sm text-gray-600">Correctas</div>
              </div>
              
              <div className="text-center">
                <div className="bg-blue-100 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <Target className="w-8 h-8 text-blue-600" />
                </div>
                <div className="text-2xl font-bold text-purple-800">{gameResults.total_questions}</div>
                <div className="text-sm text-gray-600">Preguntas</div>
              </div>
              
              <div className="text-center">
                <div className="bg-yellow-100 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <BookOpen className="w-8 h-8 text-yellow-600" />
                </div>
                <div className="text-2xl font-bold text-purple-800">{gameResults.stories_learned}</div>
                <div className="text-sm text-gray-600">Historias</div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={resetGame}
                className="btn-primary px-6 py-3 font-semibold rounded-xl flex items-center justify-center gap-2"
              >
                <RefreshCw className="w-5 h-5" />
                Estudiar Más Historias
              </button>
              
              <Link
                to="/dashboard"
                className="btn-gold px-6 py-3 font-semibold rounded-xl flex items-center justify-center gap-2"
              >
                <TrendingUp className="w-5 h-5" />
                Ver Progreso
              </Link>
            </div>

            <div className="mt-8 p-4 bg-purple-50 rounded-xl">
              <p className="text-purple-700 text-sm">
                <strong>Técnica Usada:</strong> {gameResults.percentage >= 80 
                  ? 'Excelente aplicación de codificación dual (visual + narrativa). Las enseñanzas se han fijado en tu memoria.'
                  : 'Continúa practicando con la técnica de Feynman. Explica las historias con tus propias palabras para mejor comprensión.'
                }
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default SureyeGame;